/**
 * Centro de Monitoreo del CM AI (R3.8).
 *
 * Dashboard de seguimiento para la revisión humana: centraliza qué respondió
 * el CM AI, qué no pudo responder (con motivo), los escalados, el historial de
 * revisión y una nube de temas frecuentes. Filtros por período y por etiqueta.
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { Bot, User, AlertTriangle, Inbox, MessageSquare } from "lucide-react";
import { useMonitoring } from "@/hooks/use-monitoring";
import { SUGGESTED_TAGS } from "@/hooks/use-contact";
import { formatArrivalAge } from "@/lib/format";
import { cn } from "@/lib/cn";
import type {
  MonitoringInteraction,
  MonitoringHistoryItem,
} from "@/hooks/use-monitoring";

type Period = "7d" | "30d" | "90d";
const PERIOD_DAYS: Record<Period, number> = { "7d": 7, "30d": 30, "90d": 90 };
const PERIOD_LABEL: Record<Period, string> = {
  "7d": "7 días",
  "30d": "30 días",
  "90d": "90 días",
};

export function MonitoringPage() {
  const { clientSlug } = useParams({ strict: false }) as { clientSlug: string };
  const [period, setPeriod] = useState<Period>("30d");
  const [tag, setTag] = useState<string | null>(null);

  // Rango derivado del período. `to` es "ahora" fijado al mount del cálculo.
  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - PERIOD_DAYS[period] * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [period]);

  const { data, isLoading } = useMonitoring(clientSlug, {
    from: range.from,
    to: range.to,
    tag: tag ?? undefined,
  });

  const s = data?.summary;
  const notResponded = (s?.pending ?? 0) + (s?.ignored ?? 0);

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-[1100px] px-6 py-6 space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-1.5 font-mono text-2xs uppercase tracking-wider text-text-muted mb-1">
            <Link to="/c/$clientSlug" params={{ clientSlug }} className="hover:text-text-primary">
              {clientSlug}
            </Link>
            <span>/</span>
            <span>monitoreo</span>
          </div>
          <h1 className="font-sans text-3xl font-semibold tracking-snug text-text-primary">
            Centro de Monitoreo
          </h1>
          <p className="font-sans text-sm text-text-muted mt-1">
            Seguimiento del CM AI: qué respondió, qué no pudo responder y por qué,
            escalados, historial de revisión y temas frecuentes.
          </p>
        </div>

        {/* Filtros */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="inline-flex items-center rounded-full bg-surface-2 border border-default p-[3px]">
            {(Object.keys(PERIOD_DAYS) as Period[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={cn(
                  "h-7 px-3.5 rounded-full text-xs font-sans font-medium transition-colors",
                  period === p
                    ? "bg-surface-1 text-text-primary shadow-sm"
                    : "text-text-secondary hover:text-text-primary",
                )}
              >
                {PERIOD_LABEL[p]}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-sans text-xs text-text-muted mr-1">Etiqueta:</span>
            <TagChip label="Todas" active={tag === null} onClick={() => setTag(null)} />
            {SUGGESTED_TAGS.map((t) => (
              <TagChip
                key={t}
                label={t}
                active={tag === t}
                onClick={() => setTag(tag === t ? null : t)}
              />
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="text-sm text-text-muted py-12 text-center">Cargando monitoreo…</div>
        ) : !data ? (
          <div className="text-sm text-text-muted py-12 text-center">Sin datos.</div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard icon={<Inbox size={16} />} label="Interacciones" value={s?.total ?? 0} />
              <StatCard
                icon={<Bot size={16} />}
                label="Respondió CM AI"
                value={s?.respondedByBot ?? 0}
                tone="bot"
              />
              <StatCard
                icon={<User size={16} />}
                label="Respondió humano"
                value={s?.respondedByHuman ?? 0}
                tone="human"
              />
              <StatCard
                icon={<AlertTriangle size={16} />}
                label="Escaladas"
                value={s?.escalated ?? 0}
                tone="escalated"
              />
              <StatCard
                icon={<MessageSquare size={16} />}
                label="No respondidas"
                value={notResponded}
                tone="warning"
              />
            </div>

            {/* Temas frecuentes */}
            {data.topics.length > 0 && (
              <section className="rounded-xl border border-default bg-surface-1 p-5">
                <h2 className="font-sans text-sm font-semibold text-text-primary mb-3">
                  Temas frecuentes
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                  {data.topics.map((t) => (
                    <span
                      key={t.term}
                      className="inline-flex items-center gap-1.5 rounded-full border border-default bg-surface-2 px-3 py-1 font-sans text-text-primary"
                      style={{
                        fontSize: `${Math.min(11 + t.count, 20)}px`,
                        opacity: Math.min(0.55 + t.count / 20, 1),
                      }}
                    >
                      {t.term}
                      <span className="font-mono text-2xs text-text-muted tabular-nums">
                        {t.count}
                      </span>
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* No respondidas + Escalados */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <InteractionList
                title="No pudo responder"
                subtitle="mensajes sin respuesta del bot, con el motivo"
                clientSlug={clientSlug}
                items={data.notResponded}
                variant="motive"
              />
              <InteractionList
                title="Escalados"
                subtitle="derivados a revisión humana"
                clientSlug={clientSlug}
                items={data.escalated}
                variant="escalated"
              />
            </div>

            {/* Historial de revisión */}
            <HistoryList clientSlug={clientSlug} items={data.history} />
          </>
        )}
      </div>
    </div>
  );
}

function TagChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-2xs font-sans font-medium transition-colors",
        active
          ? "border-magenta bg-magenta-soft-bg text-text-primary"
          : "border-default bg-surface-2 text-text-secondary hover:text-text-primary",
      )}
    >
      {label}
    </button>
  );
}

const TONE_ICON_CLASS: Record<string, string> = {
  bot: "text-state-bot-text",
  human: "text-state-human-text",
  escalated: "text-state-escalated-text",
  warning: "text-warning-text",
  default: "text-text-muted",
};

function StatCard({
  icon,
  label,
  value,
  tone = "default",
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone?: string;
}) {
  return (
    <div className="rounded-xl border border-default bg-surface-1 p-4">
      <div className={cn("mb-2", TONE_ICON_CLASS[tone] ?? TONE_ICON_CLASS.default)}>
        {icon}
      </div>
      <div className="font-sans text-2xl font-semibold text-text-primary tabular-nums">
        {value}
      </div>
      <div className="font-sans text-xs text-text-muted mt-0.5">{label}</div>
    </div>
  );
}

function InteractionList({
  title,
  subtitle,
  clientSlug,
  items,
  variant,
}: {
  title: string;
  subtitle: string;
  clientSlug: string;
  items: MonitoringInteraction[];
  variant: "motive" | "escalated";
}) {
  return (
    <section className="rounded-xl border border-default bg-surface-1 overflow-hidden flex flex-col">
      <header className="px-5 py-3.5 border-b border-subtle">
        <h2 className="font-sans text-sm font-semibold text-text-primary">
          {title}
          <span className="ml-2 font-mono text-2xs text-text-muted tabular-nums">
            {items.length}
          </span>
        </h2>
        <p className="font-sans text-xs text-text-muted mt-0.5">{subtitle}</p>
      </header>
      {items.length === 0 ? (
        <div className="px-5 py-8 text-sm text-text-muted text-center">
          Nada en este período.
        </div>
      ) : (
        <ul className="flex flex-col max-h-[420px] overflow-y-auto">
          {items.map((it) => (
            <InteractionRow key={it.id} it={it} clientSlug={clientSlug} variant={variant} />
          ))}
        </ul>
      )}
    </section>
  );
}

function InteractionRow({
  it,
  clientSlug,
  variant,
}: {
  it: MonitoringInteraction;
  clientSlug: string;
  variant: "motive" | "escalated";
}) {
  const inner = (
    <div className="px-5 py-3 hover:bg-surface-2 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <span className="font-sans text-sm font-medium text-text-primary truncate">
          @{it.externalUsername ?? "anon"}
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="font-sans text-2xs text-text-muted">
            {it.kind === "comment" ? "comentario" : "dm"}
          </span>
          <span className="font-mono text-[11px] tabular-nums text-text-muted">
            {formatArrivalAge(it.receivedAt)}
          </span>
        </div>
      </div>
      <p className="mt-1 font-sans text-sm text-text-secondary line-clamp-2 leading-snug">
        {it.body}
      </p>
      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        {it.category && (
          <span className="inline-flex items-center rounded-full border border-state-escalated-border bg-state-escalated-bg px-2 py-0.5 text-2xs font-sans font-medium text-state-escalated-text">
            {it.category}
          </span>
        )}
        <span
          className={cn(
            "font-sans text-2xs",
            variant === "escalated" ? "text-text-secondary" : "text-warning-text",
          )}
        >
          {variant === "escalated" ? it.reason || "Escalado" : it.motive}
        </span>
      </div>
    </div>
  );

  if (it.conversationId) {
    return (
      <li className="border-b border-subtle last:border-b-0">
        <Link
          to="/c/$clientSlug/inbox/$conversationId"
          params={{ clientSlug, conversationId: it.conversationId }}
          className="block"
        >
          {inner}
        </Link>
      </li>
    );
  }
  return <li className="border-b border-subtle last:border-b-0">{inner}</li>;
}

function HistoryList({
  clientSlug,
  items,
}: {
  clientSlug: string;
  items: MonitoringHistoryItem[];
}) {
  return (
    <section className="rounded-xl border border-default bg-surface-1 overflow-hidden">
      <header className="px-5 py-3.5 border-b border-subtle">
        <h2 className="font-sans text-sm font-semibold text-text-primary">
          Historial de revisión
          <span className="ml-2 font-mono text-2xs text-text-muted tabular-nums">
            {items.length}
          </span>
        </h2>
        <p className="font-sans text-xs text-text-muted mt-0.5">
          últimas interacciones resueltas y su estado
        </p>
      </header>
      {items.length === 0 ? (
        <div className="px-5 py-8 text-sm text-text-muted text-center">
          Sin interacciones resueltas en este período.
        </div>
      ) : (
        <ul className="flex flex-col max-h-[480px] overflow-y-auto">
          {items.map((h) => {
            const escalated = h.state === "escalated";
            const row = (
              <div className="px-5 py-3 hover:bg-surface-2 transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-sans text-sm font-medium text-text-primary truncate">
                    @{h.externalUsername ?? "anon"}
                  </span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className={cn(
                        "inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-sans font-medium border",
                        escalated
                          ? "bg-state-escalated-bg text-state-escalated-text border-state-escalated-border"
                          : "bg-success-bg text-success-text border-success-border",
                      )}
                    >
                      {escalated ? "escalado" : `respondió ${h.respondedBy ?? "—"}`}
                    </span>
                    <span className="font-mono text-[11px] tabular-nums text-text-muted">
                      {formatArrivalAge(h.receivedAt)}
                    </span>
                  </div>
                </div>
                <p className="mt-1 font-sans text-sm text-text-muted line-clamp-1 leading-snug">
                  {h.body}
                </p>
                {h.responseBody && (
                  <p className="mt-0.5 font-sans text-sm text-text-secondary line-clamp-1 leading-snug">
                    → {h.responseBody}
                  </p>
                )}
              </div>
            );
            return (
              <li key={h.id} className="border-b border-subtle last:border-b-0">
                {h.conversationId ? (
                  <Link
                    to="/c/$clientSlug/inbox/$conversationId"
                    params={{ clientSlug, conversationId: h.conversationId }}
                    className="block"
                  >
                    {row}
                  </Link>
                ) : (
                  row
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
